import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import ComplianceBanner from "@/components/ComplianceBanner";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";

export default function Partners() {
  const [form, setForm] = useState({
    full_name: "", email: "", audience: "", channels: "", website: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partners/apply", form);
      setDone(true);
      toast.success("Application received");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-enter">
      <ComplianceBanner />
      <section className="border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 pb-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline mb-4">PRMU Partners</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">
              An attribution-first <br /> referral program.
            </h1>
            <p className="mt-6 text-zinc-600 max-w-xl text-lg">
              Every partner receives a unique referral link, a dedicated coupon code, and a transparent commission ledger. Applications are reviewed manually.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { k: "15%", t: "Base commission", d: "Configurable per partner." },
                { k: "30d", t: "Attribution window", d: "Last-click, extendable." },
                { k: "Net-15", t: "Payout cadence", d: "Standard cycle." },
              ].map((c) => (
                <div key={c.k} className="border border-black/10 p-6">
                  <div className="font-display text-4xl font-bold">{c.k}</div>
                  <div className="mt-2 font-medium">{c.t}</div>
                  <div className="text-sm text-zinc-500">{c.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            {done ? (
              <div className="border border-white/10 bg-white/[0.02] rounded-xl p-8 text-center">
                <CheckCircle size={40} className="mx-auto text-blue-400" />
                <div className="font-display text-2xl font-bold mt-4 text-white">Application received</div>
                <p className="mt-2 text-sm text-zinc-400">
                  We manually review each partner. If a match, we'll email you with next steps and provision your portal access.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] rounded-xl p-6 md:p-8" data-testid="partner-form">
                <div className="overline mb-4">Apply</div>
                <div className="grid grid-cols-1 gap-4">
                  <Input label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testid="pf-name" />
                  <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="pf-email" />
                  <Input label="Audience / research focus" value={form.audience} onChange={(v) => setForm({ ...form, audience: v })} required testid="pf-audience" />
                  <Input label="Channels (site, newsletter, community)" value={form.channels} onChange={(v) => setForm({ ...form, channels: v })} required testid="pf-channels" />
                  <Input label="Website (optional)" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="pf-website" />
                  <label className="block">
                    <div className="overline mb-1">Notes</div>
                    <textarea
                      rows={4}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full border border-white/15 rounded-lg px-3 py-2 outline-none focus:border-blue-500 bg-white/[0.03] text-sm text-white"
                      data-testid="pf-notes"
                    />
                  </label>
                </div>
                <button className="btn-prm w-full mt-6" disabled={submitting} data-testid="pf-submit">
                  {submitting ? "Sending…" : "Submit application"}
                </button>
                <p className="text-xs text-zinc-500 mt-4">
                  By submitting you agree to our partner terms. Approved partners receive login credentials for a dedicated portal.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-white/15 rounded-lg px-3 py-2 outline-none focus:border-blue-500 bg-white/[0.03] text-sm text-white"
        data-testid={testid}
      />
    </label>
  );
}
